import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import BonusHistory from 'modules/bonuses/bonus-history/index.js';
import InProgressBonuses from 'modules/bonuses/in-progress-bonuses/index.js';
import ActiveBonuses from 'modules/bonuses/active-bonuses/index.js';
import FreeBetCode from 'modules/bonuses/free-bet-code/index.js';
import BonusViewTypes from './types.js';
import BonusesLoadingIndicator from 'modules/bonuses/bonuses-loading-indicator/index.js';
import { useSelector } from 'react-redux';
import { useState, useCallback, useMemo } from 'react';

const { selectors: campaignSelectors } = MojitoServices.BonusCampaign;
const { selectInprogressBonusCampaigns } = campaignSelectors;
const { BONUS_FILTER } = MojitoServices.Bonus.types;
const { AbsolutePane, FlexPane, ScrollPane, SelectableButton, SelectableButtonGroup } =
    MojitoPresentation.Components;

const SELECTED_TAB_MAP = {
    ACTIVE: 'Active bonuses',
    HISTORIC: 'Bonus history',
    IN_PROGRESS: 'In progress',
};

const hasConfiguredTab = (config, tab) => config.tabs.some(({ id }) => id === BONUS_FILTER[tab]);

function Bonuses(props) {
    const {
        defaultTab,
        onSelectedTabChange,
        mojitoTools: { config, appContext },
    } = props;
    const [selectedTab, setSelectedTab] = useState();
    const inProgressBonuses = useSelector(selectInprogressBonusCampaigns);
    const hasActiveTab = hasConfiguredTab(config, BONUS_FILTER.ACTIVE);
    const hasInProgressTab = hasConfiguredTab(config, BONUS_FILTER.IN_PROGRESS);

    const onTabChange = tab => {
        setSelectedTab(tab);
        onSelectedTabChange(tab);
    };

    const onManualTabChange = useCallback(
        id => {
            onTabChange(id);
            appContext.analyticsEmitter.emitAnalytics('bonusesTabChanged', SELECTED_TAB_MAP[id]);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useMemo(() => {
        // When navigating directly to in progress or history, we select that tab here to prevent
        // automatic switching of tabs.
        const stayOnTab =
            defaultTab === BONUS_FILTER.IN_PROGRESS || defaultTab === BONUS_FILTER.HISTORIC;
        if (stayOnTab && hasConfiguredTab(config, defaultTab)) {
            setSelectedTab(defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Automatically change tabs depending on content when there is no pre-selected (default) tab.
    // The priority order:
    // 1. Active bonuses if player has active bonuses.
    // 2. In progress bonuses if player has in progress bonuses.
    // 3. Active bonuses if player has neither active nor in progress bonuses.
    // 4. Whatever tab is configured first if active bonuses isn't configured.
    const onActiveContentLoad = useCallback(
        hasContent => {
            const hasInProgressBonuses = !!inProgressBonuses.length;
            if (!selectedTab) {
                if (hasContent && hasActiveTab) {
                    onTabChange(BONUS_FILTER.ACTIVE);
                } else if (hasInProgressBonuses && hasInProgressTab) {
                    onTabChange(BONUS_FILTER.IN_PROGRESS);
                } else {
                    onTabChange(config.tabs[0].id);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inProgressBonuses, selectedTab]
    );

    const showTabs = config.showTabSwitcher && !!config.tabs?.length;
    const content = (
        <BonusContent
            {...props}
            type={selectedTab || BONUS_FILTER.ACTIVE}
            onContentLoad={onActiveContentLoad}
        />
    );

    return (
        <FlexPane config={config.container} class="ta-Bonuses">
            <AddFreeBetCode config={config} tab={selectedTab} />
            {showTabs ? (
                <Tabs {...props} onTabClicked={onManualTabChange} selectedTab={selectedTab}>
                    {content}
                </Tabs>
            ) : (
                content
            )}
        </FlexPane>
    );
}

function AddFreeBetCode({ config, tab }) {
    const absolutelyPositioned =
        config.freeBetCodePosition === BonusViewTypes.FREE_BET_CODE_POSITION.ABSOLUTE;

    return absolutelyPositioned && tab === BONUS_FILTER.ACTIVE ? (
        <AbsolutePane config={config.absoluteFreeBetCodeContainer}>
            <FreeBetCode config={config.freeBetCode} />
        </AbsolutePane>
    ) : null;
}

function BonusContent({ mojitoTools: { config }, type, onContentLoad }) {
    let content;
    switch (type) {
        case BONUS_FILTER.ACTIVE:
            content = (
                <ActiveBonuses
                    config={config.activeBonuses}
                    showPrewager={config.showPrewager}
                    showOddsBoosts={config.showOddsBoosts}
                    onContentLoad={onContentLoad}
                />
            );
            break;
        case BONUS_FILTER.IN_PROGRESS:
            content = <InProgressBonuses config={config.inProgressBonuses} />;
            break;
        case BONUS_FILTER.HISTORIC:
            content = (
                <BonusHistory config={config.bonusHistory} showPrewager={config.showPrewager} />
            );
            break;
        default:
            content = (
                <BonusesLoadingIndicator
                    config={config.loadingIndicator}
                    labelClassName="ta-fetchingActiveBonusesText"
                />
            );
    }
    return <FlexPane config={config.contentContainer}>{content}</FlexPane>;
}

function Tabs(props) {
    const {
        mojitoTools: { config, stringResolver },
        onTabClicked,
        selectedTab,
        children,
    } = props;

    const tabs = config.tabs.map(({ id, label }) => (
        <SelectableButton key={id} selectableKey={id} class={`ta-bonusTab ${id}`}>
            {stringResolver.resolveString(label)}
        </SelectableButton>
    ));

    const showAddPromotion =
        config.freeBetCodePosition === BonusViewTypes.FREE_BET_CODE_POSITION.ON_TAB &&
        selectedTab === BONUS_FILTER.ACTIVE;

    return (
        <>
            <FlexPane config={config.tabsContainer}>
                <SelectableButtonGroup
                    config={config.tabsStyles}
                    selectedKey={selectedTab}
                    onSelectionChange={onTabClicked}
                >
                    {tabs}
                </SelectableButtonGroup>
                {showAddPromotion && <FreeBetCode config={config.freeBetCode} />}
            </FlexPane>
            <FlexPane config={config.overflowHeightReset}>
                <ScrollPane config={config.scrollPane}>{children}</ScrollPane>
            </FlexPane>
        </>
    );
}

export default Bonuses;
